/* gt-pdp.js — shared deferred JS for the Roamwild garden tool PDP sections.
   Vanilla JS only, no jQuery, no external libs (bible §2.1). Each section
   guards its own init per instance (data-gt-init) and re-inits on
   shopify:section:load for theme editor preview. Implemented so far:
   gt-main-product (§6.1: gallery, §7 autoplay, variant radios → live
   price/sold-out, §4.4(5) sticky bar, §6.7 gt:select-variant listener);
   gt-tech-slab (§6.3/§4.4(2): desktop arrow buttons scroll the snap row
   by ±352px — buttons are hidden on mobile in CSS);
   gt-specs-addon (§6.6/§4.4(3): tab switching + add-on AJAX add to cart
   with inline confirmation only — no theme drawer API assumed);
   gt-compare (§6.7: variant-mode CTA dispatches gt:select-variant and
   smooth-scrolls to the buy box). */
(function () {
  'use strict';

  /* ---------- shared helpers ---------- */

  /* Substitute the variant price (in minor units) into the store's
     money format string, e.g. "£{{amount}}" → "£44.99". */
  function formatMoney(cents, format) {
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = match ? match[1] : 'amount';
    var n = cents / 100;
    var value;
    switch (key) {
      case 'amount_no_decimals':
      case 'amount_no_decimals_with_comma_separator':
        value = String(Math.round(n));
        break;
      case 'amount_with_comma_separator':
        value = n.toFixed(2).replace('.', ',');
        break;
      default:
        value = n.toFixed(2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* ---------- gt-main-product (§6.1) ---------- */

  function initMainProduct(root) {
    var moneyFormat = root.getAttribute('data-money-format') || '{{amount}}';

    var variants = [];
    var variantsEl = root.querySelector('[data-gt-variants]');
    if (variantsEl) {
      try { variants = JSON.parse(variantsEl.textContent); } catch (err) { variants = []; }
    }

    /* ----- gallery (§4.4(1)) ----- */
    var stage = root.querySelector('[data-gt-stage]');
    var medias = stage ? stage.querySelectorAll('[data-gt-media]') : [];
    var thumbs = root.querySelectorAll('[data-gt-thumb]');
    var heroVideo = stage ? stage.querySelector('.gt-stage-video video, video.gt-stage-video') : null;
    var activeIndex = 0;

    /* §7: set muted as property AND attribute; swallow the rejected
       promise so Low Power Mode shows the poster with no broken UI. */
    function attemptPlay() {
      if (!heroVideo) return;
      heroVideo.muted = true;
      heroVideo.setAttribute('muted', '');
      var p = heroVideo.play();
      if (p) p.catch(function () {});
    }

    /* Hidden lazy images don't fetch until shown, which made thumb clicks
       feel slow. Flip them to eager once the page has loaded (keeps initial
       paint fast, makes every later swap instant). */
    function warmGallery() {
      if (!stage) return;
      stage.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        img.loading = 'eager';
      });
    }
    if (document.readyState === 'complete') {
      setTimeout(warmGallery, 300);
    } else {
      window.addEventListener('load', function () { setTimeout(warmGallery, 300); });
    }

    function showMedia(i) {
      if (!medias[i]) return;
      activeIndex = i;
      var img = medias[i].querySelector('img[loading="lazy"]');
      if (img) img.loading = 'eager'; /* fallback if warm-up hasn't run yet */
      medias.forEach(function (m, idx) { m.classList.toggle('is-active', idx === i); });
      thumbs.forEach(function (t) { t.classList.toggle('is-active', Number(t.dataset.index) === i); });
      stage.dataset.active = String(i);
      stage.querySelectorAll('video').forEach(function (v) {
        if (!medias[i].contains(v)) v.pause();
      });
      if (i === 0) attemptPlay();
    }

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { showMedia(Number(t.dataset.index)); });
    });

    var rail = root.querySelector('[data-gt-thumbs]');
    var more = root.querySelector('[data-gt-thumb-more]');
    if (more && rail) {
      more.addEventListener('click', function () {
        rail.scrollBy({ top: 200, behavior: 'smooth' });
      });
    }

    if (heroVideo) {
      attemptPlay();
      if ('IntersectionObserver' in window && stage) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (activeIndex !== 0) return;
            if (e.isIntersecting) { attemptPlay(); } else { heroVideo.pause(); }
          });
        }, { threshold: 0.25 }).observe(stage);
      }
    }

    /* ----- variant selection → live price / sold-out (§2.3) ----- */
    var priceEl = root.querySelector('[data-gt-price]');
    var compareEl = root.querySelector('[data-gt-compare-price]');
    var addBtn = root.querySelector('[data-gt-add]');
    var stickyBar = root.querySelector('[data-gt-sticky]');
    var stickyPrice = root.querySelector('[data-gt-sticky-price]');
    var stickyAdd = root.querySelector('[data-gt-sticky-add]');

    function findVariant(id) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(id)) return variants[i];
      }
      return null;
    }

    function applyVariant(id) {
      var v = findVariant(id);
      if (!v) return;
      var onSale = !!(v.compare_at_price && v.compare_at_price > v.price);
      if (priceEl) {
        priceEl.textContent = formatMoney(v.price, moneyFormat);
        priceEl.classList.toggle('is-sale', onSale);
      }
      if (compareEl) {
        if (onSale) {
          compareEl.textContent = formatMoney(v.compare_at_price, moneyFormat);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      if (stickyPrice) stickyPrice.textContent = formatMoney(v.price, moneyFormat);
      [addBtn, stickyAdd].forEach(function (btn) {
        if (!btn) return;
        btn.disabled = !v.available;
        btn.textContent = v.available
          ? (btn.getAttribute('data-label-add') || 'Add to bag')
          : (btn.getAttribute('data-label-soldout') || 'Sold out');
      });
      root.querySelectorAll('.gt-variant').forEach(function (card) {
        var input = card.querySelector('input[name="id"]');
        if (input) card.classList.toggle('is-selected', String(input.value) === String(id));
      });
    }

    root.querySelectorAll('[data-gt-variant-input]').forEach(function (input) {
      input.addEventListener('change', function () { applyVariant(input.value); });
    });

    function selectVariant(id) {
      var radio = root.querySelector('input[data-gt-variant-input][value="' + id + '"]');
      if (radio) radio.checked = true;
      var select = root.querySelector('select[data-gt-variant-input]');
      if (select) select.value = String(id);
      applyVariant(id);
    }

    /* §6.7: gt-compare dispatches gt:select-variant with the variant ID;
       this section listens. Listener self-removes once the root has been
       replaced (editor section reload). */
    function onExternalSelect(e) {
      if (!root.isConnected) {
        document.removeEventListener('gt:select-variant', onExternalSelect);
        return;
      }
      var id = e.detail && (e.detail.id || e.detail.variantId);
      if (id) selectVariant(id);
    }
    document.addEventListener('gt:select-variant', onExternalSelect);

    /* ----- sticky bar visibility (§4.4(5), mobile-only via CSS) ----- */
    var buyBox = root.querySelector('[data-gt-buy]');
    if (stickyBar && buyBox && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        stickyBar.classList.toggle('is-show', !entries[0].isIntersecting);
      }, { threshold: 0 }).observe(buyBox);
    }

    /* Hooks for the theme editor event handlers below. */
    root.gtHooks = { attemptPlay: attemptPlay, showMedia: showMedia, selectVariant: selectVariant };
  }

  /* ---------- gt-tech-slab (§6.3) ---------- */

  function initTechSlab(root) {
    var row = root.querySelector('[data-gt-tech-row]');
    if (!row) return;
    root.querySelectorAll('[data-gt-scroll]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        row.scrollBy({ left: Number(btn.dataset.gtScroll) * 352, behavior: 'smooth' });
      });
    });
  }

  /* ---------- gt-specs-addon (§6.6, §4.4(3)) ---------- */

  function initSpecsAddon(root) {
    var tabs = root.querySelectorAll('[data-gt-tab]');
    var panels = root.querySelectorAll('[data-gt-panel]');
    tabs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        tabs.forEach(function (t) { t.classList.toggle('is-active', t === btn); });
        panels.forEach(function (p) { p.classList.toggle('is-active', p.dataset.gtPanel === btn.dataset.gtTab); });
      });
    });

    /* §2.3: AJAX POST to routes.cart_add_url with the add-on's first
       available variant. Inline confirmation only (operator ruling). */
    var addBtn = root.querySelector('[data-gt-addon-add]');
    if (!addBtn) return;
    addBtn.addEventListener('click', function () {
      var variantId = addBtn.getAttribute('data-gt-addon-variant');
      var url = addBtn.getAttribute('data-gt-cart-add-url');
      if (!variantId || !url || addBtn.disabled) return;
      addBtn.disabled = true;
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      }).then(function (res) {
        if (!res.ok) throw new Error('Cart add failed: ' + res.status);
        return res.json();
      }).then(function () {
        addBtn.textContent = addBtn.getAttribute('data-label-added') || 'Added to bag';
        setTimeout(function () {
          addBtn.textContent = addBtn.getAttribute('data-label-add') || 'Add to bag';
          addBtn.disabled = false;
        }, 2000);
      }).catch(function () {
        addBtn.textContent = addBtn.getAttribute('data-label-add') || 'Add to bag';
        addBtn.disabled = false;
      });
    });
  }

  /* ---------- gt-compare (§6.7) ---------- */

  function initCompare(root) {
    root.querySelectorAll('[data-gt-pick-variant]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-gt-pick-variant');
        if (!id) return;
        document.dispatchEvent(new CustomEvent('gt:select-variant', { detail: { id: id } }));
        var main = document.querySelector('[data-gt-section="main-product"]');
        if (main) main.scrollIntoView({ behavior: 'smooth' });
      });
    });
  }

  /* ---------- init registry + theme editor events (§2.1) ---------- */

  var INITS = {
    'main-product': initMainProduct,
    'tech-slab': initTechSlab,
    'specs-addon': initSpecsAddon,
    'compare': initCompare
  };

  function initSection(root) {
    if (!root || root.dataset.gtInit === 'true') return;
    root.dataset.gtInit = 'true';
    var init = INITS[root.dataset.gtSection];
    if (init) init(root);
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-gt-section]');
    for (var i = 0; i < nodes.length; i++) initSection(nodes[i]);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:select', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('[data-gt-section]') : null;
    if (root && root.gtHooks) root.gtHooks.attemptPlay();
  });

  document.addEventListener('shopify:block:select', function (e) {
    var block = e.target;
    if (!block || !block.closest) return;
    var root = block.closest('[data-gt-section]');
    if (!root) return;
    if (block.matches('details')) block.setAttribute('open', '');
    var input = block.querySelector ? block.querySelector('input[name="id"]') : null;
    if (input && root.gtHooks) root.gtHooks.selectVariant(input.value);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
})();
